/*! abstract-player.js */
import DependencyLoader from "./dependency-loader";
import StrategyTypes from "app/strategy/StrategyTypes";
import VisibilityMonitor, { EVENTS as VISIBILITY_EVENTS } from "app/utils/VisibilityMonitor";
import PlayerEvents from "app/events/player-events";
import AdEvents from "app/events/ad-events";
import Log from "app/utils/log";
import ErrorReport from "app/utils/error-report";
import LoadTimer from "app/utils/load-timer";
import Site from "app/utils/Site";
import AdditionalCustomParameterService from "app/services/AdditionalCustomParameterService";
import AdType from "types/AdType";
import BmmrLoader from "app/lib/bmmr-asset-js/bmmr-loader";
import { CONTENT_OVERLAY_CLASS, BLP_REGION_ERROR } from "app/utils/Constant";

const log = new Log("FacadePlayer");

const REFLECTED_METHODS = [
    "getId",
    "getPlaylistIndex",
    "getHtmlChildId",
    "setAutoplay",
    "isAutoplay",
    "trackInfo",
    "transcript",
    "transcriptOn",
    "transcriptOff",
    "currentTime"
];

/**
 * @class FacadePlayer
 * @extends {DependencyLoader}
 */
class FacadePlayer extends DependencyLoader {

    /**
     * @constructs FacadePlayer
     * @param {object} rawUserOptions
     * @param {object} callbacks
     * @param {string} strategy
     * @param {object} [assetDetails]
     */
    constructor(rawUserOptions, callbacks, strategy, assetDetails = null) {
        const site = Site.getSiteName(window.location.hostname);
        super(site);
        this.userOptions = {
            site,
            ...rawUserOptions,
            facadePlayer: this
        };
        this.callbacks = callbacks;
        this.bvp = null;
        this.player = null;
        this.strategy = strategy;
        this.htmlChildId = this.userOptions && this.userOptions.htmlChildId;
        this.assetDetails = assetDetails;
        this.isAdEnabled = this.userOptions["module_ad_player"] !== "disabled";

        this._setupBmmrLoader();

        this._setupCustomParameterService();
        this.play = this.play.bind(this);
        this.visibilityMonitor = new VisibilityMonitor();
        this.on([PlayerEvents.PLAY, AdEvents.AD_STARTED], () => {
            this.playerStartedPlaying = true;
        });

        REFLECTED_METHODS.forEach((methodName) => {
            const self = this;

            this[methodName] = function() {
                if (self.bvp) {
                    return (self.bvp[methodName]).apply(self.bvp, arguments);
                }
                log.warn(`Unable to call ${ methodName } on ${ self.htmlChildId }. Please wait until after ready callback has been executed`);
            };
        });

        LoadTimer.setEnabled(this.htmlChildId, false);
        LoadTimer.trackLoadStart(this.htmlChildId, LoadTimer.loadTypes.PLAYER_INIT);

        this.setup();
    }

    /**
     * @method
     * @param callback
     * @returns {Promise.<videojs.player>}
     */
    ready(callback = function() {
    }) {
        return this.setup()
            .then((player) => {
                callback(player);
                return player;
            });
    }

    /**
     * @method
     */
    play() {
        if (!this.playerStartedPlaying && !VisibilityMonitor.isPageVisible()) {
            this._registerPlayOnPageActiveEvent();
            return;
        }

        if (this.bvp) {
            this.bvp.play();
        } else {
            this.deferExecution("play", arguments);
        }
    }

    /**
     * @method
     */
    pause() {
        this._unregisterPlayOnPageActiveEvent();

        if (this.bvp) {
            this.bvp.pause();
        } else {
            this.deferExecution("pause", arguments);
        }
    }

    /**
     * @method
     */
    mute() {
        if (this.bvp) {
            this.bvp.mute();
        } else {
            this.deferExecution("mute", arguments);
        }
    }

    /**
     * @method
     */
    unmute() {
        if (this.bvp) {
            this.bvp.unmute();
        } else {
            this.deferExecution("unmute", arguments);
        }
    }

    isPlaying() {
        if (this.bvp) {
            return this.bvp.isPlaying();
        }

        return false;
    }

    isMuted() {
        if (this.bvp) {
            return this.bvp.isMuted();
        }

        // when player is not ready just return undefined
        return undefined;
    }

    /**
     * @method
     */
    on() {
        if (this.bvp) {
            this.bvp.on.apply(this.bvp, arguments);
        } else {
            this.deferExecution("on", arguments);
        }
        return this;
    }

    /**
     * @method
     */
    off() {
        if (this.bvp) {
            this.bvp.off.apply(this.bvp, arguments);
        } else {
            this.deferExecution("off", arguments);
        }
        return this;
    }

    /**
     * @method
     */
    trigger() {
        if (this.bvp) {
            this.bvp.trigger.apply(this.bvp, arguments);
        } else {
            this.deferExecution("trigger", arguments);
        }
        return this;
    }

    /**
     * @method
     * @returns {Promise.<videojs.Player>}
     */
    setup() {
        return this.loadDependencies(() => {
            if (this.bvp) {
                return this.bvp.ready()
                    .then((player) => {
                        this.player = player;
                        return player;
                    });
            }

            if (VisibilityMonitor.isPageVisible()) {
                return this._createPlayer();
            }

            return new Promise((resolve) => {
                this.visibilityMonitor.one(VISIBILITY_EVENTS.VISIBLE, () => resolve(this._createPlayer()));
            });
        }, this.isAdEnabled);
    }

    hasClosedCaptions() {
        return this.player && this.player.textTracks() && this.player.textTracks().length > 0;
    }

    getContentOverlayEl() {
        return this.player?.el_?.querySelector(`.${ CONTENT_OVERLAY_CLASS }`);
    }

    showClosedCaptions() {
        if (this.bvp) {
            this.bvp.showClosedCaptions();
        } else {
            this.deferExecution("showClosedCaptions", arguments);
        }
    }

    hideClosedCaptions() {
        if (this.bvp) {
            this.bvp.hideClosedCaptions();
        } else {
            this.deferExecution("hideClosedCaptions", arguments);
        }
    }

    /**
     * @override
     * @method
     */
    dispose() {
        if (this.visibilityMonitor) {
            this.visibilityMonitor.dispose();
            this.visibilityMonitor = null;
        }

        if (this.bvp) {
            this.bvp.dispose();
            this.bvp = null;
        }

        this.player = this.userOptions = this.callbacks = this.assetDetails = null;
        super.dispose();
    }

    getAdditionalCustomParameterService() {
        return this._additionalCustomParameterService;
    }

    getBmmrLoader() {
        return this._bmmrLoader;
    }

    _createPlayer() {
        const strategy = getStrategyFor(this.strategy, this.userOptions);

        this.bvp = new window.BPlayer.BloombergVideoPlayer(this.userOptions, this.callbacks, strategy);
        return this.bvp.ready()
            .then((player) => {
                this.player = player;
                return player;
            });
    }

    _registerPlayOnPageActiveEvent() {
        this._unregisterPlayOnPageActiveEvent();
        this.visibilityMonitor.one(VISIBILITY_EVENTS.VISIBLE, this.play);
    }

    _unregisterPlayOnPageActiveEvent() {
        this.visibilityMonitor.off(VISIBILITY_EVENTS.VISIBLE, this.play);
    }

    _getHtmlChildId() {
        return this.htmlChildId;
    }

    _getHtmlContainer() {
        return this.userOptions.containerElement;
    }

    _setupCustomParameterService() {
        this._additionalCustomParameterService = new AdditionalCustomParameterService({ playerHtmlChildId: this.userOptions.htmlChildId });
        if (this.userOptions.autoplay && this.isAdEnabled) {
            // prefetch custom parameter data to reduce time to first frame
            this._additionalCustomParameterService.fetchData(AdType.PREROLL);
        }
    }

    _setupBmmrLoader() {
        try {
            this._bmmrLoader = new BmmrLoader(this.userOptions, this.assetDetails);
            // prefetch bmmr data to reduce time to first frame
            this._bmmrLoader.load();
        } catch (e) {
            if (e instanceof Error && e.message === BLP_REGION_ERROR) {
                this.userOptions.facadePlayer.renderBlockOverlay("content-not-allowed");
            } else {
                throw e;
            }
        }
    }
}

/**
 * @private
 * @param {string} strategyName
 * @param {object} userOptions
 */
function getStrategyFor(strategyName, userOptions) {
    switch (strategyName) {
        case StrategyTypes.SCRIPT:
            return new window.BPlayer.ScriptTagStrategy(userOptions);
        case StrategyTypes.EMBED:
            return new window.BPlayer.EmbedStrategy(userOptions);
        default:
            ErrorReport.recordGenericError(log, `Unexpected strategy ${ strategyName }`);
    }
}

export default FacadePlayer;
